import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { useMemo } from 'react';
import { AppHeader as LibAppHeader, type AppHeaderNavItem } from '@/lib/AppHeader';
import { goSearch } from '@/app/navigation';
import { UserMenu } from '@/app/UserMenu';
import { useCapability } from '@/app/CapabilitiesProvider';
import { orgRoute } from '@/app/org-route';

const NAV_PATHS: ReadonlyArray<{ key: string; label: string; path: string }> = [
  { key: 'attention', label: 'Attention', path: '/' },
  { key: 'fleet', label: 'Fleet', path: '/fleet' },
  { key: 'analyze', label: 'Analyze', path: '/analyze' },
  { key: 'reports', label: 'Reports', path: '/reports' },
  { key: 'inbox', label: 'Inbox', path: '/inbox' },
];

// AdminMenu removed — every configuration destination it used to
// expose now lives under /settings with a cap-gated left rail.
// The /admin/* routes redirect there.

/**
 * Project AppHeader: adapts the library AppHeader to TanStack Router by
 * injecting a router-aware Link as the nav renderLink. Active key is
 * derived from the current route's first path segment so /assets/...
 * still highlights "Fleet", etc.
 *
 * The "/" KeyHint shown in the Search button is now wired by
 * useGlobalShortcuts (mounted in AppShell). The button's click handler
 * still routes via goSearch so mouse users get the same behavior.
 */
export function AppHeader() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();
  const activeKey = activeKeyFor(pathname);
  // Recompute nav items every render so a slug change (e.g. switch
  // tenant via impersonation) updates the hrefs without remount.
  const navItems: AppHeaderNavItem[] = useMemo(
    () => NAV_PATHS.map((n) => ({ key: n.key, label: n.label, href: orgRoute(n.path) })),
    [],
  );

  return (
    <LibAppHeader
      activeKey={activeKey}
      navItems={navItems}
      renderLink={({ href, children, active, className }) => (
        <Link
          key={href}
          to={href}
          className={className}
          aria-current={active ? 'page' : undefined}
        >
          {children}
        </Link>
      )}
      onSearchClick={() => goSearch(navigate)}
      rightExtras={
        <>
          <RadiantMenu />
          <UserMenu />
        </>
      }
    />
  );
}

/**
 * Radiant superadmin entry. Only renders when the caller has the
 * radiant.manage_tenants cap — which no tenant-scoped role carries,
 * so this is invisible for normal operators.
 */
function RadiantMenu() {
  const canManage = useCapability('radiant.manage_tenants');
  if (!canManage) return null;
  return (
    <Link
      to="/radiant/tenants"
      className="rounded-r-default border border-gray-300 bg-gray-50 px-sp-3 py-sp-1 text-fs-meta text-gray-900 hover:border-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-info"
    >
      Radiant
    </Link>
  );
}

function activeKeyFor(pathname: string): string | undefined {
  // Strip the org-slug segment so the match logic below stays the
  // same as it was pre-scope. /acme/fleet -> /fleet.
  const tail = stripOrgSegment(pathname);
  if (tail === '/' || tail.startsWith('/alerts')) return 'attention';
  if (tail.startsWith('/fleet') || tail.startsWith('/assets')) return 'fleet';
  if (tail.startsWith('/analyze') || tail.startsWith('/sensors')) return 'analyze';
  if (tail.startsWith('/reports')) return 'reports';
  if (tail.startsWith('/inbox')) return 'inbox';
  if (tail.startsWith('/search')) return undefined;
  return undefined;
}

// stripOrgSegment removes the leading /<slug> segment from a path so
// activeKeyFor can match against the tail. Non-org-scoped paths
// (/auth, /invite, /radiant/...) are returned unchanged.
function stripOrgSegment(pathname: string): string {
  if (
    pathname === '/' ||
    pathname.startsWith('/auth') ||
    pathname.startsWith('/invite') ||
    pathname.startsWith('/radiant')
  ) {
    return pathname;
  }
  const rest = pathname.replace(/^\/[^/]+/, '');
  return rest === '' ? '/' : rest;
}
